import { BlockTypes } from "./block-types";

export interface BlockDescription {
  /** Short one-line definition */
  summary: string;
  /** What the block is — 2-3 sentence explanation */
  whatItIs: string;
  /** Common use cases and real-world examples */
  usedFor: string[];
  /** What operation types/content types this block excels at */
  bestAt: string[];
  /** Learning tips or important considerations */
  tips: string[];
}

export const blockDescriptions: Record<string, BlockDescription> = {
  [`${BlockTypes.USER}`]: {
    summary: "The origin of all traffic in the system.",
    whatItIs:
      "The User block represents end-user traffic entering your system. It is the root node of every architecture and generates the initial TPS (Transactions Per Second) that flow through your design. You cannot delete or duplicate this block.",
    usedFor: [
      "Simulating real-world user requests hitting your system",
      "Generating read and write traffic with static and dynamic content",
      "Testing how your architecture handles increasing load",
    ],
    bestAt: [
      "Generating a balanced mix of read and write operations",
      "Simulating both static content requests (images, CSS) and dynamic content (API calls, database queries)",
    ],
    tips: [
      "The User block scales its output TPS over time based on the traffic pattern you select",
      "A low success percentage on the User block means your architecture is dropping requests",
    ],
  },

  [`${BlockTypes.APP_SERVER}`]: {
    summary: "A general-purpose compute server that processes application logic.",
    whatItIs:
      "An App Server (e.g. EC2 instance, VM, bare-metal server) runs your application code. It receives requests from users or load balancers, processes business logic, and forwards data operations to databases or other services. Each instance has a fixed input capacity of 1,000 TPS.",
    usedFor: [
      "Running web application backends (Node.js, Python, Java, etc.)",
      "Processing API requests and business logic",
      "Serving as the compute layer between routing and storage tiers",
    ],
    bestAt: [
      "Balanced read/write workloads (600 read / 400 write TPS)",
      "Handling both static and dynamic content equally",
    ],
    tips: [
      "A single App Server can only handle 1,000 TPS — use multiple behind a Load Balancer for higher throughput",
      "App Servers don't process data themselves (processingCapacity is zero) — they forward to downstream storage",
      "Consider Auto Scaling Groups for automatic horizontal scaling",
    ],
  },

  [`${BlockTypes.AUTO_SCALING_GROUP}`]: {
    summary: "A group of servers that automatically scales based on load.",
    whatItIs:
      "An Auto Scaling Group (ASG) dynamically adjusts the number of server instances based on incoming traffic. When utilization exceeds 80%, it adds a new instance; when all instances are below 30% utilization, it removes one. Each instance provides 1,000 TPS of input capacity, and the total capacity grows linearly with the instance count.",
    usedFor: [
      "Handling variable traffic loads without manual intervention",
      "Ensuring high availability during traffic spikes",
      "Scales down during low traffic periods",
    ],
    bestAt: [
      "Workloads with unpredictable or bursty traffic patterns",
      "Balanced read/write operations that need elastic compute",
    ],
    tips: [
      "The 'server-count' dynamic property shows the current number of instances",
      "Scale-up threshold is 80% utilization, scale-down is 30%",
      "Pairs well with a Load Balancer upstream for traffic distribution",
    ],
  },

  [`${BlockTypes.DATABASE}`]: {
    summary: "A relational SQL database for structured data with ACID transactions.",
    whatItIs:
      "A Database block represents a traditional relational database (e.g. PostgreSQL, MySQL, Amazon RDS). It stores structured data in tables with rows and columns, supports SQL queries, and guarantees ACID transactions. It has a total capacity of 2,000 TPS with a read-heavy profile.",
    usedFor: [
      "Storing structured, relational data (users, orders, products)",
      "Complex queries with JOINs across multiple tables",
      "Transactions requiring ACID guarantees (financial, inventory)",
    ],
    bestAt: [
      "Read-heavy workloads: 1,500 read TPS (1,000 static + 500 dynamic)",
      "Strong consistency and data integrity",
      "Complex relational queries with JOINs",
    ],
    tips: [
      "Write capacity is limited to 500 TPS — a common bottleneck at scale",
      "Add a Read Replica for read scaling without affecting the primary",
      "Place a Cache (Redis) in front to offload frequent reads",
      "Cannot be connected downstream of another database — it's a terminal node",
    ],
  },

  [`${BlockTypes.NO_SQL_DB}`]: {
    summary: "A NoSQL key-value/wide-column database optimized for high throughput.",
    whatItIs:
      "A NoSQL DB (e.g. DynamoDB, Cassandra, HBase) stores data in flexible, non-relational formats like key-value pairs or wide columns. It trades the relational model and complex queries for significantly higher throughput and horizontal scalability. Total capacity is 3,250 TPS.",
    usedFor: [
      "High-throughput applications (gaming leaderboards, IoT data, session stores)",
      "Data with simple access patterns (lookups by primary key)",
      "Workloads that need to scale horizontally without complex schema migrations",
    ],
    bestAt: [
      "Static read operations: 2,000 TPS — great for caching-like lookups",
      "Write operations: 1,000 TPS — better than relational DB",
      "Simple key-based access patterns at scale",
    ],
    tips: [
      "Higher total TPS (3,250) than a relational DB (2,000) but no JOIN support",
      "Dynamic read capacity is lower (250 TPS) — complex queries aren't its strength",
      "Ideal when you don't need relational queries or complex transactions",
    ],
  },

  [`${BlockTypes.DB_READ_REPLICA}`]: {
    summary: "A read-only copy of a database for scaling read-heavy workloads.",
    whatItIs:
      "A DB Read Replica is a read-only copy of a primary database that handles read queries independently. It has an extremely high input capacity of 10,000 TPS (all reads) and is used to offload read traffic from the primary database, which can then focus on writes.",
    usedFor: [
      "Scaling read-heavy applications (e-commerce catalogs, news feeds, reporting)",
      "Offloading analytical/reporting queries from the primary database",
      "Geographic read distribution (place replicas closer to users)",
    ],
    bestAt: [
      "Static read operations: 8,000 TPS — excellent for cached/indexed data",
      "Dynamic read operations: 2,000 TPS — good for real-time queries",
      "Total read capacity of 10,000 TPS — 5x a standard database",
    ],
    tips: [
      "Cannot handle write operations at all — writes must go to the primary Database",
      "Data may be slightly stale due to replication lag (eventual consistency)",
      "Use multiple replicas behind a Load Balancer for even greater read scale",
    ],
  },

  [`${BlockTypes.LOAD_BALANCER}`]: {
    summary: "An Application Load Balancer that distributes traffic across servers.",
    whatItIs:
      "An Application Load Balancer (ALB) operates at Layer 7 (HTTP/HTTPS) and intelligently distributes incoming traffic across multiple backend servers. It has a massive input capacity of 100,000 TPS and supports traffic route configuration for path-based routing. It does not process data itself — it purely forwards.",
    usedFor: [
      "Distributing traffic evenly across multiple App Servers or Auto Scaling Groups",
      "Path-based routing (e.g. /api/* → API servers, /static/* → CDN)",
      "SSL termination and health checking of backend instances",
    ],
    bestAt: [
      "Extremely high throughput: 100,000 total TPS",
      "Balanced read/write forwarding (60,000 read / 40,000 write)",
      "Layer 7 (application-level) routing decisions",
    ],
    tips: [
      "Processing capacity is zero — it only forwards, never processes",
      "Has configurable traffic route settings (click the settings gear)",
      "Place between User and App Servers for horizontal scaling",
    ],
  },

  [`${BlockTypes.NETWORK_LOAD_BALANCER}`]: {
    summary: "A Network Load Balancer operating at Layer 4 for ultra-high throughput.",
    whatItIs:
      "A Network Load Balancer (NLB) operates at Layer 4 (TCP/UDP) and forwards traffic at the network level without inspecting application content. It offers even higher throughput than an ALB (150,000 TPS) and lower latency, but without application-aware routing features.",
    usedFor: [
      "Ultra-high-throughput, low-latency traffic distribution",
      "TCP/UDP protocol load balancing (databases, game servers, IoT)",
      "Routing to backend services that need raw network performance",
    ],
    bestAt: [
      "Maximum throughput: 150,000 total TPS (50% more than ALB)",
      "Balanced distribution: 90,000 read / 60,000 write TPS",
      "Low latency, connection-level load balancing",
    ],
    tips: [
      "Can route directly to databases and caches (unlike ALB)",
      "No application-level routing (no path-based rules)",
      "Use when you need raw throughput over intelligent routing",
    ],
  },

  [`${BlockTypes.CDN}`]: {
    summary: "A Content Delivery Network for serving static assets at massive scale.",
    whatItIs:
      "A CDN (e.g. CloudFront, Cloudflare, Akamai) is a globally distributed network of edge servers that cache and serve static content (images, CSS, JS, videos) close to end users. It has an enormous capacity of 250,000 TPS but only handles static read operations.",
    usedFor: [
      "Serving static assets (images, videos, CSS, JavaScript files)",
      "Reducing latency by caching content at edge locations worldwide",
      "Offloading static content from your origin servers",
    ],
    bestAt: [
      "Static read operations: 250,000 TPS — the highest capacity block",
      "Global content delivery with low latency",
      "Offloading massive amounts of static traffic from your backend",
    ],
    tips: [
      "Only handles static reads — dynamic content and writes pass through elsewhere",
      "Cannot accept input from App Servers — only from the User block directly",
      "Processes 250,000 TPS — incredible throughput capacity",
      "Essential for any architecture serving images, videos, or static files",
    ],
  },

  [`${BlockTypes.CACHE}`]: {
    summary: "An in-memory cache (Redis/Memcached) that accelerates reads with a warm-up period.",
    whatItIs:
      "A Cache block (e.g. Redis, Memcached, ElastiCache) stores frequently accessed data in memory for ultra-fast retrieval. It starts with zero processing capacity and 'warms up' as it sees traffic, eventually reaching a maximum of 100,000 TPS. It sits between compute and database layers.",
    usedFor: [
      "Reducing database load by caching frequent read queries",
      "Session storage for web applications",
      "Leaderboards, counters, and real-time analytics",
    ],
    bestAt: [
      "Static read operations: up to 80,000 TPS when fully warm",
      "Total throughput up to 100,000 TPS after warm-up",
      "Sub-millisecond read latency from in-memory storage",
    ],
    tips: [
      "Starts cold (zero processing) and warms up as it receives traffic — be patient",
      "The 'atMax' dynamic property indicates when the cache is fully warm",
      "Can forward cache misses to a Database or Read Replica downstream",
      "Write capacity is limited (10,000 TPS) — primarily a read accelerator",
    ],
  },

  [`${BlockTypes.LAMBDA}`]: {
    summary: "A serverless function that auto-scales per request with no server management.",
    whatItIs:
      "A Lambda function (e.g. AWS Lambda, Google Cloud Functions, Azure Functions) is a serverless compute unit that runs code in response to events. It automatically scales by spawning new instances — each handling 1,000 TPS. Spawns instances on demand, but individual instances have limited capacity.",
    usedFor: [
      "Event-driven processing (API requests, file uploads, queue messages)",
      "Microservices and API backends with variable traffic",
      "Data transformation, image processing, and background jobs",
    ],
    bestAt: [
      "Auto-scaling: spawns instances to match any traffic level",
      "Balanced workloads: 600 read / 400 write per instance",
      "Highly efficient for sporadic or bursty workloads",
    ],
    tips: [
      "The 'scale-count' dynamic property shows current instance count",
      "Resource usage scales linearly with instances",
      "Processing capacity is zero per-instance — Lambdas forward to downstream services",
    ],
  },

  [`${BlockTypes.QUEUE}`]: {
    summary: "A message queue for decoupling producers and consumers with buffering.",
    whatItIs:
      "A Queue (e.g. SQS, RabbitMQ, Kafka) is an asynchronous message buffer that decouples upstream producers from downstream consumers. It accepts bursts of write traffic (up to 1,000,000 TPS) and drains at the rate its consumers can handle. This smooths out traffic spikes and prevents downstream overload.",
    usedFor: [
      "Decoupling microservices for resilience",
      "Absorbing traffic spikes that would overwhelm downstream services",
      "Background job processing (email sending, report generation)",
    ],
    bestAt: [
      "Write buffering: 1,000,000 TPS input capacity — absorbs any burst",
      "Only accepts write/dynamic operations (not reads)",
      "Asynchronous processing with guaranteed delivery",
    ],
    tips: [
      "Only accepts dynamic write operations — it's a write buffer, not a read cache",
      "Drains at the rate downstream consumers (App Server, Lambda) can handle",
      "The 'queueState' dynamic property shows buffered messages",
      "Essential for architectures that need to handle bursty write patterns",
    ],
  },

  graph_db: {
    summary: "A graph database optimized for highly connected data structures and relationship traversal.",
    whatItIs:
      "A Graph Database (e.g. Neo4j, Amazon Neptune) stores data in nodes and edges, allowing direct relationship traversal instead of expensive JOIN operations. It has a total capacity of 3,000 TPS and is highly specialized for social graphs, recommendations, or fraud detection.",
    usedFor: [
      "Social networking applications",
      "Recommendation engines",
      "Fraud detection networks and identity resolution",
      "Knowledge graphs and pathfinding algorithms",
    ],
    bestAt: [
      "Traversing deep relationships (many-to-many connections)",
      "Handling high-volume read queries on highly connected data: 2,500 read TPS",
      "Dynamic read operations: 1,000 TPS (better than relational DB)",
    ],
    tips: [
      "Limited write capacity (500 TPS) — not ideal for high-volume transactions",
      "Does not support standard SQL queries — requires query languages like Cypher or Gremlin",
      "Usually requires an App Server or Lambda upstream to structure queries",
    ],
  },

  document_db: {
    summary: "A document-oriented NoSQL database storing JSON-like documents.",
    whatItIs:
      "A Document DB (e.g. MongoDB, DocumentDB) stores data in flexible, JSON-like documents rather than rigid tables. It supports complex queries, indexing, and high-performance reads and writes. It has a total capacity of 4,000 TPS.",
    usedFor: [
      "Content management systems (CMS)",
      "User profile stores with variable attributes",
      "Catalog management and e-commerce systems",
      "High-throughput telemetry and logging",
    ],
    bestAt: [
      "Dynamic read operations: 1,000 TPS — great for dynamic queries on complex objects",
      "Static read operations: 2,000 TPS",
      "Write operations: 1,000 TPS",
    ],
    tips: [
      "Has higher total capacity (4,000 TPS) than relational Database (2,000 TPS) and NoSQL DB (3,250 TPS)",
      "Schema-less nature allows rapid development and updates, but data validation must be handled in application logic",
      "Great choice when you need dynamic query capabilities with higher throughput than relational SQL",
    ],
  },

  s3_bucket: {
    summary: "An object storage service designed for storing large amounts of unstructured data.",
    whatItIs:
      "An S3 Bucket represents cloud object storage (e.g. AWS S3, Google Cloud Storage). It is designed to store and retrieve unstructured data files (images, backups, static files) with high durability and scaling. It has a capacity of 5,500 TPS.",
    usedFor: [
      "Storing user-uploaded media (photos, videos, documents)",
      "Static web hosting assets",
      "Backups, archives, and big data lake storage",
      "Serving as the origin storage behind a CDN",
    ],
    bestAt: [
      "High-volume static read operations: 3,500 TPS",
      "Write operations: 2,000 TPS — excellent for massive parallel file uploads",
    ],
    tips: [
      "Highest write capacity (2,000 TPS) among all storage/database blocks",
      "Cannot process dynamic query logic — purely a file/object store",
      "Place a CDN in front of S3 to cache static read requests and scale delivery further",
    ],
  },

  passthrough: {
    summary: "A generic routing and forwarding node that passes traffic downstream.",
    whatItIs:
      "A Passthrough block acts as a gateway or message router. When it has outgoing connections (children), it forwards all traffic downstream without processing (acting as a zero-latency router). When it has no children, it acts as a sink with infinite processing capacity.",
    usedFor: [
      "Routing traffic to multiple downstream services",
      "Acting as an architectural placeholder or gateway",
      "Testing traffic distribution without adding extra compute load",
    ],
    bestAt: [
      "Infinite forwarding throughput",
      "Zero-latency/zero-processing overhead",
    ],
    tips: [
      "Automatically sets its processing capacity to zero when connected to children, passing all traffic through",
      "If not connected to anything downstream, it consumes and processes all incoming traffic as a sink",
      "Highly flexible routing block for staging complex architecture routes",
    ],
  },
};
